// Message types for communication
export const MESSAGE_TYPES = {
  PAGE_READY: "page-ready",
  WIDGET_READY: "widget-ready",
  PAGE_METADATA: "page-metadata",
  PARENT_READY: "parent-ready",
  EXECUTE_TASK: "EXECUTE_TASK",
  TASK_RESULT: "TASK_RESULT",
  WIDGET_OPENED: "WIDGET_OPENED",
  OPEN_WIDGET: "OPEN_WIDGET",
  CLOSE_WIDGET: "CLOSE_WIDGET",
  REQUEST_NAVIGATION_DATA: "REQUEST_NAVIGATION_DATA",
  NAVIGATION_DATA: "NAVIGATION_DATA",
  CLEAR_NAVIGATION_FLAGS: "CLEAR_NAVIGATION_FLAGS",
  REQUEST_PLATFORM_ID: "request-platform-id",
  PLATFORM_ID: "platform-id",
} as const;

export type MessageType = (typeof MESSAGE_TYPES)[keyof typeof MESSAGE_TYPES];

// Message interfaces
export interface BaseMessage {
  type: MessageType;
  data: any;
  timestamp: number;
  source: string;
}

export interface PageReadyMessage extends BaseMessage {
  type: "page-ready";
  data: {
    version: string;
    capabilities: string[];
  };
}

export interface WidgetReadyMessage extends BaseMessage {
  type: "widget-ready";
  data: {
    version: string;
    capabilities: string[];
  };
}

export interface PageMetadataMessage extends BaseMessage {
  type: "page-metadata";
  data: {
    pageMetadata: ClickableElements[];
    requestId?: string;
  };
}

export interface ExecuteTaskMessage extends BaseMessage {
  type: "EXECUTE_TASK";
  data: {
    taskId: string;
    action: "CLICK" | "TYPE" | "NAVIGATE" | "SCROLL";
    selectors: Array<{
      type: "id" | "class" | "xpath" | "tag";
      value: string;
    }>;
    inputValue?: string;
    requiresResnap?: boolean;
  };
}

export interface TaskResultMessage extends BaseMessage {
  type: "TASK_RESULT";
  data: {
    taskId: string;
    success: boolean;
    error: string | null;
  };
}

export interface WidgetOpenedMessage extends BaseMessage {
  type: "WIDGET_OPENED";
  data: {};
}

export interface OpenWidgetMessage extends BaseMessage {
  type: "OPEN_WIDGET";
  data: {};
}

export interface CloseWidgetMessage extends BaseMessage {
  type: "CLOSE_WIDGET";
  data: {};
}

// Clickable element interface
export interface ClickableElements {
  type:
    | "button"
    | "link"
    | "input"
    | "select"
    | "textarea"
    | "div"
    | "span"
    | "other";
  text: string | null;
  value: string | null;
  position: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  selectors: Array<{
    type: "id" | "class" | "xpath" | "tag";
    value: string;
  }>;
  attributes: {
    value?: string;
    placeholder?: string;
    title?: string;
    disabled?: boolean;
    required?: boolean;
    type?: string; // for input elements
    href?: string; // for links
  };
}

export interface PageMetadataResponse {
  elements: ClickableElements[];
  currentPage: {
    url: string;
    title: string | null;
    description: string | null;
  };
}

// Utility types
export type AnyMessage =
  | PageReadyMessage
  | WidgetReadyMessage
  | PageMetadataMessage
  | ExecuteTaskMessage
  | TaskResultMessage
  | WidgetOpenedMessage
  | OpenWidgetMessage
  | CloseWidgetMessage;

export type MessageHandler = (message: AnyMessage) => void;
export type MessageCallback = (
  type: MessageType,
  data: any,
  source: string
) => void;
